import { intl } from "LocaleWrapper";
import { useApolloClient, useMutation } from "@apollo/client";

import { Card, PageHeader, Select, Table } from "@cofano/cck";
import Delete from "@mui/icons-material/Delete";

import { tableDescriptions, TableId, useBackendTable } from "components/table";
import { useConfirm } from "components/hooks/useConfirm";

import AccountPermissionSelect from "view/users/components/selects/AccountPermissionSelect";

import { GetDefaultFrontendTableSettingsDocument } from "__generated__/view/defaultTableSettings/graphql/defaultTableSettings.queries.generated";
import { DeleteDefaultFrontendTableSettingsDocument } from "__generated__/view/defaultTableSettings/graphql/defaultTableSettings.mutations.generated";
import { DefaultTableSettingsFragment } from "__generated__/view/defaultTableSettings/graphql/defaultTableSettings.fragments.generated";

const DefaultTableSettings = () => {
	const confirm = useConfirm();
	const client = useApolloClient();

	const [ deleteTableSettings, { loading: deleteLoading } ] = useMutation(DeleteDefaultFrontendTableSettingsDocument);

	const { tableProps, refetchAfterDelete } = useBackendTable({
		id: TableId.DefaultTableSettings,
		onChange: async (pagination, sort, filter, callback) => {
			try {
				const result = await client.query({
					query: GetDefaultFrontendTableSettingsDocument,
					variables: {
						pagination: {
							...pagination,
							sortList: sort,
						},
						search: {
							tableId: filter.tableKey,
							permissions: filter.permission,
						},
					},
				});

				callback({
					content: result.data.defaultFrontendTableSettings?.content.map((it) => ({
						...it,
						id: undefined,
					})) ?? [],
					totalResults: result.data.defaultFrontendTableSettings?.totalElements ?? 0,
				});
			} catch {
				callback({
					content: [],
					totalResults: 0,
				});
			}
		},
		saveForOrganizationEnabled: false,
	});

	return (
		<div>
			<PageHeader sticky>
				<PageHeader.Title>{intl.formatMessage({ id: "DEFAULT_TABLE_SETTINGS" })}</PageHeader.Title>
			</PageHeader>

			<Card disablePadding>
				<Table
					{...tableProps}
					onGetRowId={(row: DefaultTableSettingsFragment) => row.tableKey}
					contentLoading={tableProps.contentLoading || deleteLoading}
					columns={[ {
						id: "tableKey",
						title: intl.formatMessage({ id: "TABLE_KEY" }),
						render: (row: DefaultTableSettingsFragment) => tableDescriptions[row.tableKey as TableId] ?? row.tableKey,
						filterComponent: (value, onChange) => (
							<Select<TableId>
								value={value}
								onChange={onChange}
								label={intl.formatMessage({ id: "TABLE_KEY" })}
								fetchOptions={Object.values(TableId).toSorted()}
								getOptionLabel={(option) => tableDescriptions[option]}
							/>
						),
						sortable: true,
						sortKey: "id.tableKey",
					}, {
						id: "permission",
						title: intl.formatMessage({ id: "USER_TYPE" }),
						render: (row: DefaultTableSettingsFragment) => intl.formatMessage({ id: `USER_TYPE_${row.permission}` }),
						filterComponent: (value, onChange) => (
							<AccountPermissionSelect
								label={intl.formatMessage({ id: "USER_TYPE" })}
								value={value}
								onChange={onChange}
								multiple
							/>
						),
						sortable: true,
						sortKey: "id.permission",
					} ]}
					rowActions={[ {
						icon: <Delete />,
						tooltip: intl.formatMessage({ id: "DELETE" }),
						onClick: async (row: DefaultTableSettingsFragment) => {

							confirm({
								type: "DELETE",
							}).then(() => {
								deleteTableSettings({
									variables: {
										permission: row.permission,
										tableKey: row.tableKey,
									},
								}).then(() => {
									refetchAfterDelete();
								});
							});
						},
					} ]}
				/>
			</Card>
		</div>
	);
};

export default DefaultTableSettings;
